"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  LOOKSMAX_COPY_PREVIEW_VARIANT,
  isLooksmaxCopyVariantId,
  type LooksmaxCopyVariantId,
} from "@/libs/cross-sell-v2";

const STORAGE_KEY = "cross_sell_dev_preview_variant";

type CrossSellDevPreviewContextValue = {
  /** Dev localhost switcher is available */
  isDev: boolean;
  /** User picked a copy variant in the dev switcher (null = off) */
  devVariantId: LooksmaxCopyVariantId | null;
  setDevVariantId: (variantId: LooksmaxCopyVariantId | null) => void;
  /** Effective variant: dev switcher overrides the compile-time constant */
  previewVariantId: LooksmaxCopyVariantId | null;
};

const CrossSellDevPreviewContext = createContext<CrossSellDevPreviewContextValue | null>(null);

export function isCrossSellDevPreviewAvailable(): boolean {
  if (process.env.NODE_ENV !== "development") return false;
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]";
}

function readStoredVariant(): LooksmaxCopyVariantId | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (raw === "off" || raw == null) return null;
  if (isLooksmaxCopyVariantId(raw)) return raw;
  // Unknown value → off (deal banner). Dev switcher can force a looksmax variant.
  return null;
}

export function CrossSellDevPreviewProvider({ children }: { children: ReactNode }) {
  const [isDev, setIsDev] = useState(false);
  const [devVariantId, setDevVariantIdState] = useState<LooksmaxCopyVariantId | null>(null);

  useEffect(() => {
    const available = isCrossSellDevPreviewAvailable();
    setIsDev(available);
    if (available) {
      setDevVariantIdState(readStoredVariant());
    }
  }, []);

  const setDevVariantId = useCallback((variantId: LooksmaxCopyVariantId | null) => {
    setDevVariantIdState(variantId);
    if (typeof window === "undefined") return;
    sessionStorage.setItem(STORAGE_KEY, variantId ?? "off");
  }, []);

  const previewVariantId = useMemo(() => {
    if (isDev && devVariantId) return devVariantId;
    return LOOKSMAX_COPY_PREVIEW_VARIANT;
  }, [isDev, devVariantId]);

  const value = useMemo(
    () => ({
      isDev,
      devVariantId,
      setDevVariantId,
      previewVariantId,
    }),
    [isDev, devVariantId, setDevVariantId, previewVariantId],
  );

  return (
    <CrossSellDevPreviewContext.Provider value={value}>
      {children}
    </CrossSellDevPreviewContext.Provider>
  );
}

export function useCrossSellDevPreview(): CrossSellDevPreviewContextValue {
  const ctx = useContext(CrossSellDevPreviewContext);
  if (!ctx) {
    return {
      isDev: false,
      devVariantId: null,
      setDevVariantId: () => {},
      previewVariantId: LOOKSMAX_COPY_PREVIEW_VARIANT,
    };
  }
  return ctx;
}
